import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "../../styles/common/popup.scss";
import dragIcon from "../../assets/svg/dropIcon.svg";

const CreateMaterialPopup = ({
  open,
  handleClose,
  folderId,
  onSave,
  mode = "create",
  initialType = "pdf",
  initialName = "",
  initialLink = "",
  materialId,
  material,
}) => {
  const [type, setType] = useState("pdf");
  const [name, setName] = useState("");
  const [link, setLink] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [fileError, setFileError] = useState("");

  const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

  const isValidLink = (value) => {
    if (!value.trim()) return true;

    try {
      const url = new URL(value.trim());
      return url.protocol === "http:" || url.protocol === "https:";
    } catch (_) {
      return false;
    }
  };

  const getAcceptedFileTypes = () => {
    switch (type) {
      case "pdf":
        return "application/pdf,.pdf";
      case "ppt":
        return ".ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation";
      case "videos":
        return "video/*";
      case "audio":
        return "audio/*";
      case "image":
        return "image/*";
      default:
        return undefined;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  useEffect(() => {
    setFile(null);
    setFileError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [type]);

  const isValidFileType = (file, type) => {
    const mime = file.type;

    switch (type) {
      case "pdf":
        return mime === "application/pdf";

      case "ppt":
        return (
          mime === "application/vnd.ms-powerpoint" ||
          mime ===
            "application/vnd.openxmlformats-officedocument.presentationml.presentation"
        );

      case "videos":
        return mime.startsWith("video/");

      case "audio":
        return mime.startsWith("audio/");

      case "image":
        return mime.startsWith("image/");

      default:
        return false;
    }
  };

  useEffect(() => {
    if (open) {
      if (mode === "edit") {
        setType(material?.materials_type || "pdf");
        setName(material?.name || "");
        setLink(material?.content || "");
      } else {
        setType("pdf");
        setName("");
        setLink("");
      }
      setFile(null);
      setSubmitting(false);
    }
  }, [open, mode, initialType, initialName, initialLink]);

  const handleMakeEmpty = () => {
    setName("");
    setLink("");
    setFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !folderId ||
      !name.trim() ||
      (!link.trim() && !file) ||
      !isValidLink(link) ||
      fileError
    )
      return;
    if (submitting) return;
    setSubmitting(true);

    const payload = {
      // id: materialId,
      materials_folders: folderId,
      name: name.trim(),
      materials_type: type,
      content: link.trim(),
    };

    try {
      await onSave(payload, file);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];

      if (droppedFile.size > MAX_FILE_SIZE_BYTES) {
        setFile(null);
        setFileError("File size must be 10MB or less");
        return;
      }

      if (!isValidFileType(droppedFile, type)) {
        setFileError(`Invalid file type for ${type.toUpperCase()}`);
        return;
      }

      setFileError("");
      setFile(droppedFile);
      setLink("");
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];

      if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
        setFile(null);
        setFileError("File size must be 10MB or less");
        e.target.value = "";
        return;
      }

      if (!isValidFileType(selectedFile, type)) {
        setFile(null);
        setFileError(`Invalid file type for ${type.toUpperCase()}`);
        e.target.value = "";
        return;
      }

      setFileError("");
      setFile(selectedFile);
      setLink("");
    }
  };

  const removeFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleLinkChange = (e) => {
    const newLink = e.target.value;
    setLink(newLink);
    if (newLink) {
      setFile(null);
      setFileError("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  if (!open) return null;

  const isSaveDisabled =
    submitting ||
    !folderId ||
    !name.trim() ||
    (!link.trim() && !file) ||
    !isValidLink(link) ||
    Boolean(fileError);

  const linkError =
    link.trim() && !isValidLink(link)
      ? "Enter a valid URL starting with http:// or https://"
      : "";

  return createPortal(
    <div
      className={`common-popup-overlay ${open ? "" : "hidden"} high-z-index`}
      onClick={handleClose}
    >
      <div
        className="common-popup-container material-popup-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="popup-header">
          <h2>{mode === "edit" ? "Edit Material" : "Create Material"}</h2>
          <button
            type="button"
            className="popup-close-button"
            onClick={handleClose}
          >
            ×
          </button>
        </div>

        <div className="popup-content">
          <form onSubmit={handleSubmit}>
            <div className="popup-form-group">
              <label htmlFor="material-type">Type</label>
              <select
                id="material-type"
                value={type}
                onChange={(e) => {
                  setType(e.target.value);
                  handleMakeEmpty();
                }}
                className="popup-input"
              >
                <option value="pdf">PDF</option>
                <option value="videos">Video</option>
                <option value="audio">Audio</option>
                <option value="image">Images</option>
                <option value="ppt">PowerPoint</option>
              </select>
            </div>

            <div className="popup-form-group">
              <label htmlFor="material-name">Name</label>
              <input
                id="material-name"
                type="text"
                className="popup-input"
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div
              className={`popup-form-group ${linkError ? "has-error" : ""}`}
            >
              <label htmlFor="material-link">Link</label>
              <input
                id="material-link"
                type="text"
                className="popup-input"
                placeholder="URL / Link"
                value={link}
                onChange={handleLinkChange}
                disabled={!!file}
                aria-invalid={Boolean(linkError)}
                aria-describedby={linkError ? "material-link-error" : undefined}
              />
              {linkError && (
                <p id="material-link-error" className="file-error-text">
                  {linkError}
                </p>
              )}
            </div>

            <div className="popup-form-group">
              <label>Upload File</label>
              <div
                className={`drop-area ${dragActive ? "drag-active" : ""} ${
                  link.trim() && isValidLink(link) ? "disabled" : ""
                } ${file ? "has-file" : ""}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={triggerFileInput}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileChange}
                  accept={getAcceptedFileTypes()}
                  style={{ display: "none" }}
                  disabled={Boolean(link.trim()) && isValidLink(link)}
                />

                {!file ? (
                  <div className="upload-ui">
                    <div className="upload-icon">
                      <img
                        src={dragIcon}
                        alt="Upload"
                        width={48}
                        height={48}
                        draggable={false}
                      />
                    </div>

                    <p className="upload-text">
                      Drag & drop files or <span>Browse</span>
                    </p>
                  </div>
                ) : (
                  <div className="upload-progress">
                    <div className="file-row">
                      <div className="file-details">
                        <span className="file-name" title={file.name}>
                          {file.name}
                        </span>
                        <span className="file-size">
                          {formatFileSize(file.size)}
                        </span>
                      </div>

                      <button
                        type="button"
                        className="remove-file"
                        aria-label={`Remove ${file.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile();
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {fileError && <p className="file-error-text">{fileError}</p>}
            </div>

            <div className="popup-actions">
              <button
                type="submit"
                className="popup-submit-button"
                disabled={isSaveDisabled}
              >
                {mode === "edit" ? "Save Changes" : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateMaterialPopup;
